import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';
import { getApiClient } from '@/services/apiClient';
import type {
  ActivityFeed,
  DashboardOverview,
} from '@/services/dashboardService';
import { dashboardService } from '@/services/dashboardService';
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { format, subDays } from 'date-fns';
import {
  Activity,
  AlertCircle,
  AlertTriangle,
  ArrowRight,
  Box,
  Building,
  Calendar,
  CheckCircle,
  CheckCircle2,
  Clock,
  DollarSign,
  FileText,
  Globe,
  MapPin,
  Package,
  Plane,
  RefreshCw,
  Rocket,
  Sparkles,
  Star,
  Target,
  TrendingDown,
  TrendingUp,
  Truck,
  Users,
  Zap,
} from 'lucide-react';
import {
  memo,
  createContext,
  lazy,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
} from 'react';
import { useNavigate } from 'react-router-dom';

const DATE_ONLY_REGEX = /^\d{4}-\d{2}-\d{2}$/;

const parseDateOnly = (value: string): Date | null => {
  if (!DATE_ONLY_REGEX.test(value)) {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  const [year, month, day] = value.split('-').map(Number);
  const parsed = new Date(year, month - 1, day);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const usePrefersReducedMotion = () => {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return;
    }

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const updatePreference = () => setPrefersReducedMotion(mediaQuery.matches);

    updatePreference();

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', updatePreference);
      return () => mediaQuery.removeEventListener('change', updatePreference);
    }

    mediaQuery.addListener(updatePreference);
    return () => mediaQuery.removeListener(updatePreference);
  }, []);

  return prefersReducedMotion;
};

const PrefersReducedMotionContext = createContext(false);

const useReducedMotionPreference = () =>
  useContext(PrefersReducedMotionContext);

// Animated counter hook for smooth number transitions
const useAnimatedCounter = (endValue: number, duration = 1000) => {
  const prefersReducedMotion = useReducedMotionPreference();
  const [count, setCount] = useState(0);
  const countRef = useRef(0);
  const rafRef = useRef<number>();

  useEffect(() => {
    const updateCount = (value: number) => {
      if (countRef.current === value) {
        return;
      }
      setCount(value);
      countRef.current = value;
    };

    if (!Number.isFinite(endValue)) {
      updateCount(0);
      return;
    }

    if (endValue === countRef.current) {
      updateCount(endValue);
      return;
    }

    if (prefersReducedMotion || duration <= 0) {
      updateCount(endValue);
      return;
    }

    const startTime = performance.now();
    const startValue = countRef.current;

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const currentValue = Math.floor(
        startValue + (endValue - startValue) * easeOutQuart
      );

      if (currentValue !== countRef.current) {
        setCount(currentValue);
        countRef.current = currentValue;
      }

      if (progress < 1) {
        rafRef.current = requestAnimationFrame(animate);
      }
    };

    rafRef.current = requestAnimationFrame(animate);

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [endValue, duration, prefersReducedMotion]);

  return count;
};

const formatStatusLabel = (status: string) => status.replace(/_/g, ' ');
const formatActivityTime = (value: string) => {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '--:--';
  }
  return format(parsed, 'HH:mm');
};

const getStatusBadgeClass = (status: string) => {
  switch (status) {
    case 'DELIVERED':
      return 'bg-green-100 text-green-800';
    case 'IN_TRANSIT':
      return 'bg-blue-100 text-blue-800';
    case 'OUT_FOR_DELIVERY':
      return 'bg-purple-100 text-purple-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const clampPercentage = (value: number) =>
  Math.min(100, Math.max(0, value));

const ACTIVITY_LIMIT = 6;

type IconType = typeof Package;

type TrendIndicator = {
  icon: IconType;
  color: string;
  bgColor: string;
};

type TrendIndicators = {
  awb: TrendIndicator;
  revenue: TrendIndicator;
  customer: TrendIndicator;
  manifest: TrendIndicator;
};

type CompanyOption = {
  value: string;
  label: string;
};

type DashboardActivityItem = ActivityFeed['activities'][number] & {
  displayTime: string;
  statusLabel: string;
  statusBadgeClass: string;
};

const EMPTY_ACTIVITY_ITEMS: DashboardActivityItem[] = [];

// Animated stat card component
const AnimatedStatValue = memo(
  ({
    value,
    prefix = '',
    suffix = '',
  }: {
    value: number;
    prefix?: string;
    suffix?: string;
  }) => {
    const animatedValue = useAnimatedCounter(value, 1200);
    return (
      <span>
        {prefix}
        {animatedValue.toLocaleString()}
        {suffix}
      </span>
    );
  }
);

AnimatedStatValue.displayName = 'AnimatedStatValue';

// Welcome Hero Banner Component
type WelcomeHeroProps = {
  userName: string;
  totalShipments: number;
  delivered: number;
  inTransit: number;
  onTimeDelivery: number;
};

const WelcomeHero = memo(
  ({
    userName,
    totalShipments,
    delivered,
    inTransit,
    onTimeDelivery,
  }: WelcomeHeroProps) => (
    <div className='relative overflow-hidden rounded-2xl bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-700 p-6 sm:p-8 text-white shadow-xl'>
      {/* Background decorations */}
      <div className='absolute top-0 right-0 -mt-4 -mr-4 h-24 w-24 rounded-full bg-white/10 blur-3xl' />
      <div className='absolute bottom-0 left-0 -mb-8 -ml-8 h-32 w-32 rounded-full bg-white/10 blur-3xl' />
      <div className='absolute top-1/2 right-1/4 h-16 w-16 rounded-full bg-white/5 blur-2xl' />

      {/* Floating icons */}
      <div className='absolute top-4 right-8 opacity-20 animate-pulse motion-reduce:animate-none'>
        <Plane className='h-8 w-8 transform -rotate-12' />
      </div>
      <div
        className='absolute bottom-8 right-24 opacity-20 animate-bounce motion-reduce:animate-none'
        style={{ animationDuration: '3s' }}
      >
        <Box className='h-6 w-6' />
      </div>
      <div
        className='absolute top-12 right-32 opacity-20 animate-pulse motion-reduce:animate-none'
        style={{ animationDelay: '1s' }}
      >
        <Globe className='h-5 w-5' />
      </div>

      <div className='relative z-10'>
        <div className='flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6'>
          {/* Welcome text */}
          <div className='space-y-2'>
            <div className='flex items-center gap-2'>
              <Sparkles className='h-5 w-5 text-yellow-300 animate-pulse motion-reduce:animate-none' />
              <span className='text-sm font-medium text-blue-100'>
                Dashboard Overview
              </span>
            </div>
            <h1 className='text-2xl sm:text-3xl lg:text-4xl font-bold'>
              Welcome back, {userName}!
            </h1>
            <p className='text-blue-100 text-sm sm:text-base max-w-md'>
              Here's what's happening with your shipments today. Keep up the
              great work!
            </p>
          </div>

          {/* Quick stats grid */}
          <div className='grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4'>
            <div className='bg-white/15 backdrop-blur-sm rounded-xl p-3 sm:p-4 text-center border border-white/10 hover:bg-white/20 transition-colors'>
              <div className='flex items-center justify-center mb-2'>
                <Package className='h-5 w-5 text-blue-200' />
              </div>
              <div className='text-xl sm:text-2xl font-bold'>
                <AnimatedStatValue value={totalShipments} />
              </div>
              <div className='text-xs text-blue-100 mt-1'>Total Shipments</div>
            </div>

            <div className='bg-white/15 backdrop-blur-sm rounded-xl p-3 sm:p-4 text-center border border-white/10 hover:bg-white/20 transition-colors'>
              <div className='flex items-center justify-center mb-2'>
                <CheckCircle2 className='h-5 w-5 text-green-300' />
              </div>
              <div className='text-xl sm:text-2xl font-bold'>
                <AnimatedStatValue value={delivered} />
              </div>
              <div className='text-xs text-blue-100 mt-1'>Delivered</div>
            </div>

            <div className='bg-white/15 backdrop-blur-sm rounded-xl p-3 sm:p-4 text-center border border-white/10 hover:bg-white/20 transition-colors'>
              <div className='flex items-center justify-center mb-2'>
                <Truck className='h-5 w-5 text-amber-300' />
              </div>
              <div className='text-xl sm:text-2xl font-bold'>
                <AnimatedStatValue value={inTransit} />
              </div>
              <div className='text-xs text-blue-100 mt-1'>In Transit</div>
            </div>

            <div className='bg-white/15 backdrop-blur-sm rounded-xl p-3 sm:p-4 text-center border border-white/10 hover:bg-white/20 transition-colors'>
              <div className='flex items-center justify-center mb-2'>
                <Zap className='h-5 w-5 text-yellow-300' />
              </div>
              <div className='text-xl sm:text-2xl font-bold'>
                <AnimatedStatValue value={onTimeDelivery} suffix='%' />
              </div>
              <div className='text-xs text-blue-100 mt-1'>On-Time</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
);

WelcomeHero.displayName = 'WelcomeHero';

type DashboardKpisProps = {
  overview: DashboardOverview['overview'];
  trends: DashboardOverview['trends'];
  quickStats: DashboardOverview['quickStats'];
  performance: DashboardOverview['performance'];
  trendIndicators: TrendIndicators;
  deliveredPercent: number;
};

const DashboardKpis = memo(
  ({
    overview,
    trends,
    quickStats,
    performance,
    trendIndicators,
    deliveredPercent,
  }: DashboardKpisProps) => (
    <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4'>
      {/* Total Shipments */}
      <Card className='group relative overflow-hidden hover:shadow-xl transition-all duration-300 border-0 bg-gradient-to-br from-blue-50 to-blue-100/50'>
        <div className='absolute top-0 right-0 w-20 h-20 bg-blue-500/10 rounded-full -mr-6 -mt-6 group-hover:scale-150 transition-transform duration-500' />
        <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
          <CardTitle className='text-sm font-medium text-blue-900'>
            Total Shipments
          </CardTitle>
          <div className='p-2 bg-blue-500 rounded-xl shadow-lg shadow-blue-500/30'>
            <Package className='h-4 w-4 text-white' />
          </div>
        </CardHeader>
        <CardContent>
          <div className='text-3xl font-bold text-blue-700'>
            <AnimatedStatValue value={overview.totalAWBs} />
          </div>
          <p className='text-xs text-blue-600/70 mt-1'>Active AWBs in system</p>
          <div className='flex items-center mt-3 pt-3 border-t border-blue-200/50'>
            {(() => {
              const trend = trendIndicators.awb;
              const TrendIcon = trend.icon;
              return (
                <div className='flex items-center gap-2'>
                  <div className={`p-1.5 rounded-lg ${trend.bgColor}`}>
                    <TrendIcon className={`h-3 w-3 ${trend.color}`} />
                  </div>
                  <span className={`text-xs font-semibold ${trend.color}`}>
                    {trends.awbTrend}
                  </span>
                  <span className='text-xs text-muted-foreground'>
                    vs last period
                  </span>
                </div>
              );
            })()}
          </div>
        </CardContent>
      </Card>

      {/* Delivered */}
      <Card className='group relative overflow-hidden hover:shadow-xl transition-all duration-300 border-0 bg-gradient-to-br from-emerald-50 to-green-100/50'>
        <div className='absolute top-0 right-0 w-20 h-20 bg-emerald-500/10 rounded-full -mr-6 -mt-6 group-hover:scale-150 transition-transform duration-500' />
        <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
          <CardTitle className='text-sm font-medium text-emerald-900'>
            Delivered
          </CardTitle>
          <div className='p-2 bg-emerald-500 rounded-xl shadow-lg shadow-emerald-500/30'>
            <CheckCircle2 className='h-4 w-4 text-white' />
          </div>
        </CardHeader>
        <CardContent>
          <div className='text-3xl font-bold text-emerald-700'>
            <AnimatedStatValue value={quickStats.delivered || 0} />
          </div>
          <p className='text-xs text-emerald-600/70 mt-1'>
            Successfully delivered
          </p>
          <div className='mt-3 pt-3 border-t border-emerald-200/50'>
            <div className='flex items-center justify-between mb-1'>
              <span className='text-xs text-emerald-600'>Completion</span>
              <span className='text-xs font-bold text-emerald-700'>
                {deliveredPercent}%
              </span>
            </div>
            <div className='w-full bg-emerald-200 rounded-full h-2 overflow-hidden'>
              <div
                className='bg-gradient-to-r from-emerald-500 to-green-500 h-2 rounded-full transition-all duration-700 ease-out'
                style={{ width: `${deliveredPercent}%` }}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* In Transit */}
      <Card className='group relative overflow-hidden hover:shadow-xl transition-all duration-300 border-0 bg-gradient-to-br from-amber-50 to-orange-100/50'>
        <div className='absolute top-0 right-0 w-20 h-20 bg-amber-500/10 rounded-full -mr-6 -mt-6 group-hover:scale-150 transition-transform duration-500' />
        <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
          <CardTitle className='text-sm font-medium text-amber-900'>
            In Transit
          </CardTitle>
          <div className='p-2 bg-amber-500 rounded-xl shadow-lg shadow-amber-500/30'>
            <Truck className='h-4 w-4 text-white' />
          </div>
        </CardHeader>
        <CardContent>
          <div className='text-3xl font-bold text-amber-700'>
            <AnimatedStatValue value={quickStats.inTransit} />
          </div>
          <p className='text-xs text-amber-600/70 mt-1'>Packages on the way</p>
          <div className='flex items-center mt-3 pt-3 border-t border-amber-200/50 gap-2'>
            <div className='p-1.5 bg-amber-100 rounded-lg'>
              <Rocket className='h-3 w-3 text-amber-600' />
            </div>
            <span className='text-xs font-semibold text-amber-700'>
              {quickStats.outForDelivery}
            </span>
            <span className='text-xs text-amber-600/70'>out for delivery</span>
          </div>
        </CardContent>
      </Card>

      {/* Total Revenue */}
      <Card className='group relative overflow-hidden hover:shadow-xl transition-all duration-300 border-0 bg-gradient-to-br from-violet-50 to-purple-100/50'>
        <div className='absolute top-0 right-0 w-20 h-20 bg-violet-500/10 rounded-full -mr-6 -mt-6 group-hover:scale-150 transition-transform duration-500' />
        <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
          <CardTitle className='text-sm font-medium text-violet-900'>
            Total Revenue
          </CardTitle>
          <div className='p-2 bg-violet-500 rounded-xl shadow-lg shadow-violet-500/30'>
            <DollarSign className='h-4 w-4 text-white' />
          </div>
        </CardHeader>
        <CardContent>
          <div className='text-3xl font-bold text-violet-700'>
            ₹<AnimatedStatValue value={overview.totalValue} />
          </div>
          <p className='text-xs text-violet-600/70 mt-1'>Shipment value</p>
          <div className='flex items-center mt-3 pt-3 border-t border-violet-200/50'>
            {(() => {
              const trend = trendIndicators.revenue;
              const TrendIcon = trend.icon;
              return (
                <div className='flex items-center gap-2'>
                  <div className={`p-1.5 rounded-lg ${trend.bgColor}`}>
                    <TrendIcon className={`h-3 w-3 ${trend.color}`} />
                  </div>
                  <span className={`text-xs font-semibold ${trend.color}`}>
                    {trends.revenueTrend}
                  </span>
                  <span className='text-xs text-muted-foreground'>growth</span>
                </div>
              );
            })()}
          </div>
        </CardContent>
      </Card>

      {/* Issues */}
      <Card className='group relative overflow-hidden hover:shadow-xl transition-all duration-300 border-0 bg-gradient-to-br from-rose-50 to-red-100/50'>
        <div className='absolute top-0 right-0 w-20 h-20 bg-rose-500/10 rounded-full -mr-6 -mt-6 group-hover:scale-150 transition-transform duration-500' />
        <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
          <CardTitle className='text-sm font-medium text-rose-900'>
            Issues
          </CardTitle>
          <div className='p-2 bg-rose-500 rounded-xl shadow-lg shadow-rose-500/30'>
            <AlertTriangle className='h-4 w-4 text-white' />
          </div>
        </CardHeader>
        <CardContent>
          <div className='text-3xl font-bold text-rose-700'>
            <AnimatedStatValue value={quickStats.issues} />
          </div>
          <p className='text-xs text-rose-600/70 mt-1'>Need attention</p>
          <div className='flex items-center mt-3 pt-3 border-t border-rose-200/50 gap-2'>
            <div className='p-1.5 bg-blue-100 rounded-lg'>
              <Clock className='h-3 w-3 text-blue-600' />
            </div>
            <span className='text-xs font-semibold text-blue-700'>
              {performance.avgDeliveryDays}
            </span>
            <span className='text-xs text-muted-foreground'>
              avg days delivery
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Performance */}
      <Card className='group relative overflow-hidden hover:shadow-xl transition-all duration-300 border-0 bg-gradient-to-br from-cyan-50 to-teal-100/50'>
        <div className='absolute top-0 right-0 w-20 h-20 bg-cyan-500/10 rounded-full -mr-6 -mt-6 group-hover:scale-150 transition-transform duration-500' />
        <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
          <CardTitle className='text-sm font-medium text-cyan-900'>
            Performance
          </CardTitle>
          <div className='p-2 bg-cyan-500 rounded-xl shadow-lg shadow-cyan-500/30'>
            <Target className='h-4 w-4 text-white' />
          </div>
        </CardHeader>
        <CardContent>
          <div className='text-3xl font-bold text-cyan-700'>
            <AnimatedStatValue value={performance.onTimeDelivery} suffix='%' />
          </div>
          <p className='text-xs text-cyan-600/70 mt-1'>On-time delivery</p>
          <div className='flex items-center mt-3 pt-3 border-t border-cyan-200/50'>
            <div className='flex items-center gap-1'>
              {[1, 2, 3, 4, 5].map(star => (
                <Star
                  key={star}
                  className={`h-3.5 w-3.5 ${
                    star <= performance.customerSatisfaction
                      ? 'text-amber-400 fill-amber-400'
                      : 'text-gray-300'
                  }`}
                />
              ))}
              <span className='text-xs font-semibold text-cyan-700 ml-1'>
                {performance.customerSatisfaction}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
);

DashboardKpis.displayName = 'DashboardKpis';

const DashboardCharts = lazy(
  () => import('@/components/dashboard/DashboardCharts')
);

const DashboardChartsFallback = () => (
  <div className='grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6'>
    {[1, 2].map(index => (
      <Card
        key={index}
        className='animate-pulse motion-reduce:animate-none overflow-hidden'
      >
        <div className='h-12 bg-gradient-to-r from-gray-100 to-gray-50'></div>
        <CardContent className='pt-6'>
          <div className='h-72 bg-gray-100 rounded-xl'></div>
        </CardContent>
      </Card>
    ))}
  </div>
);

type DashboardPerformanceProps = {
  performance: DashboardOverview['performance'];
  overview: DashboardOverview['overview'];
  quickStats: DashboardOverview['quickStats'];
  issuePercent: number;
};

const DashboardPerformance = memo(
  ({
    performance,
    overview,
    quickStats,
    issuePercent,
  }: DashboardPerformanceProps) => (
    <Card className='overflow-hidden'>
      <CardHeader className='bg-gradient-to-r from-rose-50 to-orange-50 border-b'>
        <CardTitle className='text-lg flex items-center'>
          <div className='p-2 bg-gradient-to-br from-rose-500 to-orange-500 rounded-xl mr-3 shadow-lg shadow-rose-500/30'>
            <Target className='h-4 w-4 text-white' />
          </div>
          Performance Metrics
        </CardTitle>
        <CardDescription>Key operational indicators</CardDescription>
      </CardHeader>
      <CardContent className='pt-4'>
        <div className='space-y-4'>
          {/* On-Time Delivery */}
          <div className='group p-4 rounded-xl bg-gradient-to-r from-emerald-50/80 to-white border border-emerald-100 hover:border-emerald-200 hover:shadow-md transition-all duration-300'>
            <div className='flex items-center justify-between mb-3'>
              <div className='flex items-center space-x-3'>
                <div className='p-2.5 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl shadow-lg shadow-emerald-500/30 group-hover:scale-110 transition-transform duration-300'>
                  <CheckCircle className='h-4 w-4 text-white' />
                </div>
                <div>
                  <span className='text-sm font-semibold text-gray-900'>
                    On-Time Delivery Rate
                  </span>
                  <p className='text-xs text-muted-foreground'>
                    Performance vs target
                  </p>
                </div>
              </div>
              <span className='text-2xl font-bold text-emerald-600'>
                {performance.onTimeDelivery}%
              </span>
            </div>
            <div className='w-full bg-emerald-100 rounded-full h-2.5 overflow-hidden'>
              <div
                className='bg-gradient-to-r from-emerald-500 to-green-500 h-2.5 rounded-full transition-all duration-700 ease-out'
                style={{
                  width: `${clampPercentage(performance.onTimeDelivery)}%`,
                }}
              />
            </div>
          </div>

          {/* Average Delivery Time */}
          <div className='group p-4 rounded-xl bg-gradient-to-r from-blue-50/80 to-white border border-blue-100 hover:border-blue-200 hover:shadow-md transition-all duration-300'>
            <div className='flex items-center justify-between'>
              <div className='flex items-center space-x-3'>
                <div className='p-2.5 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-lg shadow-blue-500/30 group-hover:scale-110 transition-transform duration-300'>
                  <Clock className='h-4 w-4 text-white' />
                </div>
                <div>
                  <span className='text-sm font-semibold text-gray-900'>
                    Average Delivery Time
                  </span>
                  <p className='text-xs text-muted-foreground'>
                    Days from pickup to delivery
                  </p>
                </div>
              </div>
              <div className='text-right'>
                <span className='text-2xl font-bold text-blue-600'>
                  {performance.avgDeliveryDays}
                </span>
                <span className='text-sm text-blue-600 ml-1'>days</span>
              </div>
            </div>
          </div>

          {/* Customer Satisfaction */}
          <div className='group p-4 rounded-xl bg-gradient-to-r from-amber-50/80 to-white border border-amber-100 hover:border-amber-200 hover:shadow-md transition-all duration-300'>
            <div className='flex items-center justify-between'>
              <div className='flex items-center space-x-3'>
                <div className='p-2.5 bg-gradient-to-br from-amber-500 to-yellow-500 rounded-xl shadow-lg shadow-amber-500/30 group-hover:scale-110 transition-transform duration-300'>
                  <Star className='h-4 w-4 text-white' />
                </div>
                <div>
                  <span className='text-sm font-semibold text-gray-900'>
                    Customer Satisfaction
                  </span>
                  <p className='text-xs text-muted-foreground'>
                    Based on delivery performance
                  </p>
                </div>
              </div>
              <div className='text-right'>
                <span className='text-2xl font-bold text-amber-600'>
                  {performance.customerSatisfaction}
                </span>
                <span className='text-sm text-amber-600'>/5.0</span>
                <div className='flex items-center justify-end mt-1 gap-0.5'>
                  {[1, 2, 3, 4, 5].map(star => (
                    <Star
                      key={star}
                      className={`h-3.5 w-3.5 ${
                        star <= performance.customerSatisfaction
                          ? 'text-amber-400 fill-amber-400'
                          : 'text-gray-300'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Total Weight & Issues in a row */}
          <div className='grid grid-cols-2 gap-3'>
            <div className='group p-4 rounded-xl bg-gradient-to-r from-purple-50/80 to-white border border-purple-100 hover:border-purple-200 hover:shadow-md transition-all duration-300'>
              <div className='flex items-center space-x-3 mb-2'>
                <div className='p-2 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-lg shadow-md shadow-purple-500/30 group-hover:scale-110 transition-transform duration-300'>
                  <Package className='h-3.5 w-3.5 text-white' />
                </div>
                <span className='text-xs font-medium text-gray-600'>
                  Weight Handled
                </span>
              </div>
              <div className='text-xl font-bold text-purple-600'>
                {overview.totalWeight.toFixed(1)}{' '}
                <span className='text-sm font-medium'>kg</span>
              </div>
            </div>

            <div className='group p-4 rounded-xl bg-gradient-to-r from-rose-50/80 to-white border border-rose-100 hover:border-rose-200 hover:shadow-md transition-all duration-300'>
              <div className='flex items-center space-x-3 mb-2'>
                <div className='p-2 bg-gradient-to-br from-rose-500 to-red-600 rounded-lg shadow-md shadow-rose-500/30 group-hover:scale-110 transition-transform duration-300'>
                  <AlertTriangle className='h-3.5 w-3.5 text-white' />
                </div>
                <span className='text-xs font-medium text-gray-600'>
                  Open Issues
                </span>
              </div>
              <div className='text-xl font-bold text-rose-600'>
                {quickStats.issues}{' '}
                <span className='text-xs font-normal text-muted-foreground'>
                  ({issuePercent}%)
                </span>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
);

DashboardPerformance.displayName = 'DashboardPerformance';

type DashboardActivityFeedProps = {
  isLoading: boolean;
  activities: DashboardActivityItem[];
};

const DashboardActivityFeed = memo(
  ({ isLoading, activities }: DashboardActivityFeedProps) => (
    <Card className='overflow-hidden'>
      <CardHeader className='bg-gradient-to-r from-indigo-50 to-blue-50 border-b'>
        <CardTitle className='text-lg flex items-center'>
          <div className='p-2 bg-indigo-500 rounded-xl mr-3 shadow-lg shadow-indigo-500/30'>
            <Activity className='h-4 w-4 text-white' />
          </div>
          Real-time Activity
        </CardTitle>
        <CardDescription>Latest updates across your operations</CardDescription>
      </CardHeader>
      <CardContent className='pt-4'>
        <div className='space-y-3 max-h-[380px] overflow-y-auto pr-1 custom-scrollbar'>
          {isLoading ? (
            <div className='space-y-3'>
              {[1, 2, 3, 4].map(i => (
                <div
                  key={i}
                  className='animate-pulse motion-reduce:animate-none flex items-center space-x-3 p-4 bg-gray-50/80 rounded-xl'
                >
                  <div className='w-10 h-10 bg-gray-200 rounded-full'></div>
                  <div className='flex-1'>
                    <div className='h-3 bg-gray-200 rounded w-3/4 mb-2'></div>
                    <div className='h-2 bg-gray-200 rounded w-1/2'></div>
                  </div>
                </div>
              ))}
            </div>
          ) : activities.length > 0 ? (
            activities.map((activity, index) => (
              <div
                key={`${activity.awbNumber}-${activity.eventDate}-${activity.eventType}`}
                className='group flex items-start space-x-3 p-4 bg-gradient-to-r from-gray-50/80 to-white rounded-xl hover:from-blue-50/50 hover:to-white transition-all duration-300 border border-gray-100/50 hover:border-blue-200/50 hover:shadow-md cursor-pointer'
              >
                <div className='relative flex-shrink-0'>
                  <div className='w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center shadow-lg shadow-blue-500/30'>
                    <Package className='h-4 w-4 text-white' />
                  </div>
                  <div className='absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white flex items-center justify-center'>
                    <div className='w-1.5 h-1.5 bg-white rounded-full animate-pulse motion-reduce:animate-none' />
                  </div>
                </div>
                <div className='flex-1 min-w-0'>
                  <div className='flex items-center justify-between mb-1'>
                    <p className='text-sm font-semibold text-gray-900 group-hover:text-blue-700 transition-colors truncate'>
                      AWB #{activity.awbNumber}
                    </p>
                    <span className='text-xs text-muted-foreground bg-gray-100 px-2 py-0.5 rounded-md'>
                      {activity.displayTime}
                    </span>
                  </div>
                  <p className='text-sm text-gray-600 truncate mb-1.5'>
                    {activity.eventDescription}
                  </p>
                  <div className='flex items-center justify-between'>
                    <div className='flex items-center gap-1.5'>
                      <Users className='h-3 w-3 text-gray-400' />
                      <p className='text-xs text-muted-foreground truncate'>
                        {activity.customer}
                      </p>
                    </div>
                    <span
                      className={`text-xs px-2.5 py-1 rounded-full font-semibold ${activity.statusBadgeClass} shadow-sm`}
                    >
                      {activity.statusLabel}
                    </span>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className='text-center py-12'>
              <div className='w-16 h-16 bg-gray-100 rounded-full mx-auto mb-4 flex items-center justify-center'>
                <Activity className='h-8 w-8 text-gray-400' />
              </div>
              <p className='text-sm font-medium text-gray-600'>
                No recent activity
              </p>
              <p className='text-xs text-muted-foreground mt-1 max-w-xs mx-auto'>
                Activity will appear here as shipments are processed
              </p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
);

DashboardActivityFeed.displayName = 'DashboardActivityFeed';

type DashboardActivityFeedContainerProps = {
  dateFrom: string;
  dateTo: string;
  company?: string;
};

const DashboardActivityFeedContainer = memo(
  ({ dateFrom, dateTo, company }: DashboardActivityFeedContainerProps) => {
    const { data: activityData, isLoading } = useQuery<ActivityFeed>({
      queryKey: ['dashboard-activity', ACTIVITY_LIMIT, dateFrom, dateTo, company ?? 'all'],
      queryFn: ({ signal }) =>
        dashboardService.getActivityFeed({
          limit: ACTIVITY_LIMIT,
          includeDocuments: false,
          company,
          dateFrom,
          dateTo,
          signal,
        }),
      refetchInterval: 2 * 60 * 1000, // Refresh every 2 minutes
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
      placeholderData: keepPreviousData,
    });

    const activityItems = useMemo(() => {
      const activities = activityData?.activities;
      if (!activities || activities.length === 0) {
        return EMPTY_ACTIVITY_ITEMS;
      }
      return activities.map(activity => ({
        ...activity,
        displayTime: formatActivityTime(activity.eventDate),
        statusLabel: formatStatusLabel(activity.currentStatus),
        statusBadgeClass: getStatusBadgeClass(activity.currentStatus),
      }));
    }, [activityData?.activities]);

    return (
      <DashboardActivityFeed
        isLoading={isLoading}
        activities={activityItems}
      />
    );
  }
);

DashboardActivityFeedContainer.displayName = 'DashboardActivityFeedContainer';

type DashboardQuickActionsProps = {
  onCreateAwb: () => void;
  onAddCustomer: () => void;
  onTrackShipment: () => void;
  onOpenManifests: () => void;
};

const DashboardQuickActions = memo(
  ({
    onCreateAwb,
    onAddCustomer,
    onTrackShipment,
    onOpenManifests,
  }: DashboardQuickActionsProps) => (
    <Card className='overflow-hidden'>
      <CardHeader className='bg-gradient-to-r from-emerald-50 to-teal-50 border-b'>
        <CardTitle className='text-lg flex items-center'>
          <div className='p-2 bg-emerald-500 rounded-xl mr-3 shadow-lg shadow-emerald-500/30'>
            <Zap className='h-4 w-4 text-white' />
          </div>
          Quick Actions
        </CardTitle>
        <CardDescription>Frequently used operations</CardDescription>
      </CardHeader>
      <CardContent className='pt-4'>
        <div className='grid grid-cols-2 gap-3'>
          <button
            onClick={onCreateAwb}
            className='group relative overflow-hidden p-5 rounded-xl border-2 border-blue-100 bg-gradient-to-br from-blue-50 to-white hover:border-blue-300 hover:shadow-lg hover:shadow-blue-500/10 transition-all duration-300'
          >
            <div className='absolute top-0 right-0 w-16 h-16 bg-blue-500/10 rounded-full -mr-4 -mt-4 group-hover:scale-150 transition-transform duration-500' />
            <div className='relative z-10'>
              <div className='p-3 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl w-fit mb-3 shadow-lg shadow-blue-500/30 group-hover:scale-110 transition-transform duration-300'>
                <Package className='h-5 w-5 text-white' />
              </div>
              <div className='text-left'>
                <div className='text-sm font-semibold text-gray-900 group-hover:text-blue-700 transition-colors'>
                  Create AWB
                </div>
                <div className='text-xs text-muted-foreground'>
                  New shipment
                </div>
              </div>
            </div>
            <ArrowRight className='absolute bottom-4 right-4 h-4 w-4 text-blue-400 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300' />
          </button>

          <button
            onClick={onAddCustomer}
            className='group relative overflow-hidden p-5 rounded-xl border-2 border-emerald-100 bg-gradient-to-br from-emerald-50 to-white hover:border-emerald-300 hover:shadow-lg hover:shadow-emerald-500/10 transition-all duration-300'
          >
            <div className='absolute top-0 right-0 w-16 h-16 bg-emerald-500/10 rounded-full -mr-4 -mt-4 group-hover:scale-150 transition-transform duration-500' />
            <div className='relative z-10'>
              <div className='p-3 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl w-fit mb-3 shadow-lg shadow-emerald-500/30 group-hover:scale-110 transition-transform duration-300'>
                <Users className='h-5 w-5 text-white' />
              </div>
              <div className='text-left'>
                <div className='text-sm font-semibold text-gray-900 group-hover:text-emerald-700 transition-colors'>
                  Add Customer
                </div>
                <div className='text-xs text-muted-foreground'>New client</div>
              </div>
            </div>
            <ArrowRight className='absolute bottom-4 right-4 h-4 w-4 text-emerald-400 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300' />
          </button>

          <button
            onClick={onTrackShipment}
            className='group relative overflow-hidden p-5 rounded-xl border-2 border-amber-100 bg-gradient-to-br from-amber-50 to-white hover:border-amber-300 hover:shadow-lg hover:shadow-amber-500/10 transition-all duration-300'
          >
            <div className='absolute top-0 right-0 w-16 h-16 bg-amber-500/10 rounded-full -mr-4 -mt-4 group-hover:scale-150 transition-transform duration-500' />
            <div className='relative z-10'>
              <div className='p-3 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl w-fit mb-3 shadow-lg shadow-amber-500/30 group-hover:scale-110 transition-transform duration-300'>
                <MapPin className='h-5 w-5 text-white' />
              </div>
              <div className='text-left'>
                <div className='text-sm font-semibold text-gray-900 group-hover:text-amber-700 transition-colors'>
                  Track Shipment
                </div>
                <div className='text-xs text-muted-foreground'>
                  Find package
                </div>
              </div>
            </div>
            <ArrowRight className='absolute bottom-4 right-4 h-4 w-4 text-amber-400 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300' />
          </button>

          <button
            onClick={onOpenManifests}
            className='group relative overflow-hidden p-5 rounded-xl border-2 border-purple-100 bg-gradient-to-br from-purple-50 to-white hover:border-purple-300 hover:shadow-lg hover:shadow-purple-500/10 transition-all duration-300'
          >
            <div className='absolute top-0 right-0 w-16 h-16 bg-purple-500/10 rounded-full -mr-4 -mt-4 group-hover:scale-150 transition-transform duration-500' />
            <div className='relative z-10'>
              <div className='p-3 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl w-fit mb-3 shadow-lg shadow-purple-500/30 group-hover:scale-110 transition-transform duration-300'>
                <FileText className='h-5 w-5 text-white' />
              </div>
              <div className='text-left'>
                <div className='text-sm font-semibold text-gray-900 group-hover:text-purple-700 transition-colors'>
                  Manifests
                </div>
                <div className='text-xs text-muted-foreground'>
                  Dispatch docs
                </div>
              </div>
            </div>
            <ArrowRight className='absolute bottom-4 right-4 h-4 w-4 text-purple-400 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300' />
          </button>
        </div>
      </CardContent>
    </Card>
  )
);

DashboardQuickActions.displayName = 'DashboardQuickActions';

type DashboardSystemOverviewProps = {
  overview: DashboardOverview['overview'];
  trends: DashboardOverview['trends'];
  trendIndicators: TrendIndicators;
};

const DashboardSystemOverview = memo(
  ({ overview, trends, trendIndicators }: DashboardSystemOverviewProps) => (
    <Card className='overflow-hidden'>
      <CardHeader className='bg-gradient-to-r from-slate-50 to-gray-50 border-b'>
        <CardTitle className='text-lg flex items-center'>
          <div className='p-2 bg-slate-600 rounded-xl mr-3 shadow-lg shadow-slate-500/30'>
            <Globe className='h-4 w-4 text-white' />
          </div>
          System Overview
        </CardTitle>
        <CardDescription>Master data and system health</CardDescription>
      </CardHeader>
      <CardContent className='pt-4'>
        <div className='space-y-4'>
          <div className='grid grid-cols-2 gap-3'>
            <div className='group flex items-center justify-between p-4 bg-gradient-to-br from-blue-50 to-white rounded-xl border border-blue-100 hover:border-blue-200 hover:shadow-md transition-all duration-300'>
              <div className='flex items-center space-x-3'>
                <div className='p-2 bg-blue-500 rounded-lg group-hover:scale-110 transition-transform duration-300'>
                  <Users className='h-4 w-4 text-white' />
                </div>
                <div>
                  <span className='text-sm font-semibold text-gray-900'>
                    Customers
                  </span>
                  <p className='text-xs text-muted-foreground'>
                    Active clients
                  </p>
                </div>
              </div>
              <span className='text-2xl font-bold text-blue-600'>
                {overview.totalCustomers}
              </span>
            </div>

            <div className='group flex items-center justify-between p-4 bg-gradient-to-br from-emerald-50 to-white rounded-xl border border-emerald-100 hover:border-emerald-200 hover:shadow-md transition-all duration-300'>
              <div className='flex items-center space-x-3'>
                <div className='p-2 bg-emerald-500 rounded-lg group-hover:scale-110 transition-transform duration-300'>
                  <Building className='h-4 w-4 text-white' />
                </div>
                <div>
                  <span className='text-sm font-semibold text-gray-900'>
                    Companies
                  </span>
                  <p className='text-xs text-muted-foreground'>
                    Active partners
                  </p>
                </div>
              </div>
              <span className='text-2xl font-bold text-emerald-600'>
                {overview.totalCompanies}
              </span>
            </div>
          </div>

          <div className='grid grid-cols-2 gap-3'>
            <div className='group flex items-center justify-between p-4 bg-gradient-to-br from-purple-50 to-white rounded-xl border border-purple-100 hover:border-purple-200 hover:shadow-md transition-all duration-300'>
              <div className='flex items-center space-x-3'>
                <div className='p-2 bg-purple-500 rounded-lg group-hover:scale-110 transition-transform duration-300'>
                  <FileText className='h-4 w-4 text-white' />
                </div>
                <div>
                  <span className='text-sm font-semibold text-gray-900'>
                    Manifests
                  </span>
                  <p className='text-xs text-muted-foreground'>Dispatch docs</p>
                </div>
              </div>
              <span className='text-2xl font-bold text-purple-600'>
                {overview.totalManifests}
              </span>
            </div>

            <div className='group flex items-center justify-between p-4 bg-gradient-to-br from-amber-50 to-white rounded-xl border border-amber-100 hover:border-amber-200 hover:shadow-md transition-all duration-300'>
              <div className='flex items-center space-x-3'>
                <div className='p-2 bg-amber-500 rounded-lg group-hover:scale-110 transition-transform duration-300'>
                  <FileText className='h-4 w-4 text-white' />
                </div>
                <div>
                  <span className='text-sm font-semibold text-gray-900'>
                    Documents
                  </span>
                  <p className='text-xs text-muted-foreground'>Files stored</p>
                </div>
              </div>
              <span className='text-2xl font-bold text-amber-600'>
                {overview.documentsCount}
              </span>
            </div>
          </div>

          <div className='mt-2 pt-4 border-t border-gray-100'>
            <h4 className='text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2'>
              <Activity className='h-4 w-4 text-gray-500' />
              System Health
            </h4>
            <div className='space-y-3'>
              <div className='flex items-center justify-between p-2.5 bg-gray-50/50 rounded-lg'>
                <span className='text-sm text-muted-foreground'>
                  Data Freshness
                </span>
                <div className='flex items-center gap-2'>
                  <div className='relative'>
                    <div className='w-2.5 h-2.5 bg-green-500 rounded-full'></div>
                    <div className='absolute inset-0 w-2.5 h-2.5 bg-green-500 rounded-full animate-ping motion-reduce:animate-none opacity-75'></div>
                  </div>
                  <span className='text-sm text-green-600 font-semibold'>
                    Live
                  </span>
                </div>
              </div>
              <div className='flex items-center justify-between p-2.5 bg-gray-50/50 rounded-lg'>
                <span className='text-sm text-muted-foreground'>
                  Customer Growth
                </span>
                <div className='flex items-center gap-2'>
                  {(() => {
                    const trend = trendIndicators.customer;
                    const TrendIcon = trend.icon;
                    return (
                      <>
                        <TrendIcon className={`h-4 w-4 ${trend.color}`} />
                        <span
                          className={`text-sm font-semibold ${trend.color}`}
                        >
                          {trends.customerTrend}
                        </span>
                      </>
                    );
                  })()}
                </div>
              </div>
              <div className='flex items-center justify-between p-2.5 bg-gray-50/50 rounded-lg'>
                <span className='text-sm text-muted-foreground'>
                  Manifest Activity
                </span>
                <div className='flex items-center gap-2'>
                  {(() => {
                    const trend = trendIndicators.manifest;
                    const TrendIcon = trend.icon;
                    return (
                      <>
                        <TrendIcon className={`h-4 w-4 ${trend.color}`} />
                        <span
                          className={`text-sm font-semibold ${trend.color}`}
                        >
                          {trends.manifestTrend}
                        </span>
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
);

DashboardSystemOverview.displayName = 'DashboardSystemOverview';

const getTrendIndicator = (trendValue: string) => {
  const numValue = Number.parseFloat(trendValue.replace('%', ''));
  if (numValue > 0) {
    return {
      icon: TrendingUp,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
    };
  } else if (numValue < 0) {
    return { icon: TrendingDown, color: 'text-red-600', bgColor: 'bg-red-50' };
  }
  return { icon: TrendingUp, color: 'text-gray-600', bgColor: 'bg-gray-50' };
};

const Dashboard = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const prefersReducedMotion = usePrefersReducedMotion();
  const apiClient = useMemo(() => getApiClient(), []);
  const [companyFilter, setCompanyFilter] = useState('all');
  const [dateRange, setDateRange] = useState(() => ({
    from: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
    to: format(new Date(), 'yyyy-MM-dd'),
  }));
  const normalizedCompany = useMemo(
    () => (companyFilter === 'all' ? undefined : companyFilter),
    [companyFilter]
  );

  const { data: companyOptions = [], isLoading: isCompanyLoading } =
    useQuery<CompanyOption[]>({
      queryKey: ['dashboard-company-dropdown'],
      queryFn: async () => {
        const response = await apiClient.get(
          '/api/master-data/company/dropdown'
        );
        return response.data.data ?? [];
      },
      staleTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
    });

  // Fetch dashboard overview data
  const {
    data: dashboardData,
    isLoading: isDashboardLoading,
    error: dashboardError,
  } = useQuery<DashboardOverview>({
    queryKey: [
      'dashboard-overview',
      dateRange.from,
      dateRange.to,
      normalizedCompany ?? 'all',
    ],
    queryFn: ({ signal }) =>
      dashboardService.getDashboardOverview({
        dateFrom: dateRange.from,
        dateTo: dateRange.to,
        company: normalizedCompany,
        includeRecentActivity: false,
        signal,
      }),
    refetchInterval: 5 * 60 * 1000, // Refresh every 5 minutes
    staleTime: 2 * 60 * 1000, // Consider stale after 2 minutes
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });

  // Handle manual refresh
  const handleRefresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['dashboard-overview'] });
    queryClient.invalidateQueries({ queryKey: ['dashboard-activity'] });
    toast({
      title: 'Refreshing Dashboard',
      description: 'Fetching latest data...',
    });
  }, [queryClient, toast]);

  const handleFromChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const nextFrom = event.target.value;
      if (!nextFrom || !DATE_ONLY_REGEX.test(nextFrom)) {
        return;
      }
      setDateRange(prev => {
        const nextTo = prev.to && nextFrom > prev.to ? nextFrom : prev.to;
        if (prev.from === nextFrom && prev.to === nextTo) {
          return prev;
        }
        return { from: nextFrom, to: nextTo };
      });
    },
    []
  );

  const handleToChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const nextTo = event.target.value;
    if (!nextTo || !DATE_ONLY_REGEX.test(nextTo)) {
      return;
    }
    setDateRange(prev => {
      const nextFrom = prev.from && nextTo < prev.from ? nextTo : prev.from;
      if (prev.from === nextFrom && prev.to === nextTo) {
        return prev;
      }
      return { from: nextFrom, to: nextTo };
    });
  }, []);

  // Error handling
  useEffect(() => {
    if (dashboardError) {
      toast({
        title: 'Error Loading Dashboard',
        description: 'Failed to load dashboard data. Please try again.',
        variant: 'destructive',
      });
    }
  }, [dashboardError, toast]);

  // Memoized values - MUST be before early returns to respect Rules of Hooks
  const lastUpdatedAt = useMemo(() => {
    if (dashboardData?.generatedAt) {
      return new Date(dashboardData.generatedAt);
    }
    return new Date();
  }, [dashboardData?.generatedAt]);

  const lastUpdatedLabel = useMemo(
    () => format(lastUpdatedAt, 'MMM dd, yyyy HH:mm'),
    [lastUpdatedAt]
  );

  // Prepare revenue trend data for chart
  const revenueChartData = useMemo(
    () =>
      (dashboardData?.revenueTrend ?? []).map(item => {
        const parsedDate = parseDateOnly(item._id);
        return {
          date: parsedDate ? format(parsedDate, 'MMM dd') : item._id,
          revenue: item.revenue,
          shipments: item.count,
        };
      }),
    [dashboardData?.revenueTrend]
  );

  // Prepare status distribution data for pie chart
  const statusChartData = useMemo(
    () =>
      (dashboardData?.statusDistribution ?? []).map(item => ({
        status: item.status,
        label: formatStatusLabel(item.status),
        value: item.count,
        percentage: item.percentage,
      })),
    [dashboardData?.statusDistribution]
  );

  const trendIndicators = useMemo(
    () => ({
      awb: getTrendIndicator(dashboardData?.trends?.awbTrend ?? '0%'),
      revenue: getTrendIndicator(dashboardData?.trends?.revenueTrend ?? '0%'),
      customer: getTrendIndicator(dashboardData?.trends?.customerTrend ?? '0%'),
      manifest: getTrendIndicator(dashboardData?.trends?.manifestTrend ?? '0%'),
    }),
    [
      dashboardData?.trends?.awbTrend,
      dashboardData?.trends?.revenueTrend,
      dashboardData?.trends?.customerTrend,
      dashboardData?.trends?.manifestTrend,
    ]
  );

  const deliveredPercent = useMemo(() => {
    const totalAWBs = dashboardData?.overview?.totalAWBs ?? 0;
    const delivered = dashboardData?.quickStats?.delivered ?? 0;
    if (totalAWBs <= 0) {
      return 0;
    }
    return clampPercentage(
      Number(((delivered / totalAWBs) * 100).toFixed(1))
    );
  }, [
    dashboardData?.overview?.totalAWBs,
    dashboardData?.quickStats?.delivered,
  ]);

  const issuePercent = useMemo(() => {
    const totalAWBs = dashboardData?.overview?.totalAWBs ?? 0;
    const issues = dashboardData?.quickStats?.issues ?? 0;
    if (totalAWBs <= 0) {
      return 0;
    }
    return clampPercentage(Number(((issues / totalAWBs) * 100).toFixed(1)));
  }, [dashboardData?.overview?.totalAWBs, dashboardData?.quickStats?.issues]);

  const handleCreateAwb = useCallback(() => navigate('/awb'), [navigate]);
  const handleAddCustomer = useCallback(
    () => navigate('/master-data'),
    [navigate]
  );
  const handleTrackShipment = useCallback(
    () => navigate('/tracking'),
    [navigate]
  );
  const handleOpenManifests = useCallback(
    () => navigate('/manifests'),
    [navigate]
  );

  let content: JSX.Element;
  if (isDashboardLoading) {
    content = (
      <div className='space-y-6'>
        {/* Loading Hero Banner */}
        <div className='animate-pulse motion-reduce:animate-none rounded-2xl bg-gradient-to-r from-blue-200 via-indigo-200 to-purple-200 p-8 h-48'>
          <div className='flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6'>
            <div className='space-y-3'>
              <div className='h-4 bg-white/50 rounded w-32'></div>
              <div className='h-8 bg-white/50 rounded w-64'></div>
              <div className='h-3 bg-white/50 rounded w-48'></div>
            </div>
            <div className='grid grid-cols-4 gap-4'>
              {[1, 2, 3, 4].map(i => (
                <div
                  key={i}
                  className='bg-white/30 rounded-xl p-4 w-24 h-20'
                ></div>
              ))}
            </div>
          </div>
        </div>

        {/* Loading Date Controls */}
        <div className='animate-pulse motion-reduce:animate-none bg-gray-100 p-4 rounded-xl h-16'></div>

        {/* Loading KPI Cards */}
        <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4'>
          {[1, 2, 3, 4, 5, 6].map(i => (
            <Card
              key={i}
              className='animate-pulse motion-reduce:animate-none overflow-hidden'
            >
              <div className='h-2 bg-gradient-to-r from-gray-200 to-gray-100'></div>
              <CardHeader className='space-y-2'>
                <div className='h-4 bg-gray-200 rounded w-3/4'></div>
                <div className='h-8 bg-gray-200 rounded w-1/2'></div>
              </CardHeader>
              <CardContent>
                <div className='h-2 bg-gray-200 rounded w-full'></div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Loading Charts */}
        <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
          {[1, 2].map(i => (
            <Card
              key={i}
              className='animate-pulse motion-reduce:animate-none overflow-hidden'
            >
              <div className='h-12 bg-gradient-to-r from-gray-100 to-gray-50'></div>
              <CardContent className='pt-6'>
                <div className='h-72 bg-gray-100 rounded-xl'></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  } else if (!dashboardData) {
    content = (
      <div className='flex items-center justify-center h-64'>
        <div className='text-center'>
          <AlertCircle className='h-12 w-12 text-muted-foreground mx-auto mb-4' />
          <h2 className='text-lg font-semibold'>No Data Available</h2>
          <p className='text-muted-foreground'>Unable to load dashboard data</p>
          <Button onClick={handleRefresh} className='mt-4'>
            <RefreshCw className='h-4 w-4 mr-2' />
            Try Again
          </Button>
        </div>
      </div>
    );
  } else {
    content = (
      <div className='space-y-6'>
        {/* Welcome Hero Banner */}
        <WelcomeHero
          userName={user?.name || 'User'}
          totalShipments={dashboardData.overview.totalAWBs}
          delivered={dashboardData.quickStats.delivered || 0}
          inTransit={dashboardData.quickStats.inTransit}
          onTimeDelivery={dashboardData.performance.onTimeDelivery}
        />

        {/* Date Range and Refresh Controls */}
        <div className='flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white/50 backdrop-blur-sm p-4 rounded-xl border border-gray-100 shadow-sm'>
          <div className='flex items-center gap-3'>
            <div className='p-2 bg-gray-100 rounded-lg'>
              <Calendar className='h-4 w-4 text-gray-600' />
            </div>
            <div>
              <p className='text-sm font-medium text-gray-700'>Date Range</p>
              <p className='text-xs text-muted-foreground'>
                Last updated: {lastUpdatedLabel}
              </p>
            </div>
          </div>

          <div className='flex flex-col sm:flex-row gap-3 items-center'>
            <Select
              value={companyFilter}
              onValueChange={setCompanyFilter}
              disabled={isCompanyLoading}
            >
              <SelectTrigger
                className='w-52 bg-white border-gray-200 focus:border-blue-300 focus:ring-blue-200'
                aria-label='Select company'
              >
                <SelectValue
                  placeholder={
                    isCompanyLoading ? 'Loading companies...' : 'All companies'
                  }
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='all'>All companies</SelectItem>
                {companyOptions.map(company => (
                  <SelectItem key={company.value} value={company.value}>
                    {company.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className='flex gap-2 items-center'>
              <Input
                type='date'
                value={dateRange.from}
                onChange={handleFromChange}
                className='w-36 bg-white border-gray-200 focus:border-blue-300 focus:ring-blue-200'
                max={dateRange.to}
              />
              <span className='text-muted-foreground text-sm'>to</span>
              <Input
                type='date'
                value={dateRange.to}
                onChange={handleToChange}
                className='w-36 bg-white border-gray-200 focus:border-blue-300 focus:ring-blue-200'
                min={dateRange.from}
              />
            </div>

            <Button
              onClick={handleRefresh}
              variant='outline'
              size='sm'
              className='bg-white hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700 transition-all duration-200'
            >
              <RefreshCw className='h-4 w-4 mr-2' />
              Refresh
            </Button>
          </div>
        </div>

        {/* KPI Cards */}
        <DashboardKpis
          overview={dashboardData.overview}
          trends={dashboardData.trends}
          quickStats={dashboardData.quickStats}
          performance={dashboardData.performance}
          trendIndicators={trendIndicators}
          deliveredPercent={deliveredPercent}
        />

        {/* Charts */}
        <Suspense fallback={<DashboardChartsFallback />}>
          <DashboardCharts
            revenueChartData={revenueChartData}
            statusChartData={statusChartData}
            prefersReducedMotion={prefersReducedMotion}
          />
        </Suspense>

        {/* Performance and Activity */}
        <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
          <DashboardPerformance
            performance={dashboardData.performance}
            overview={dashboardData.overview}
            quickStats={dashboardData.quickStats}
            issuePercent={issuePercent}
          />
          <DashboardActivityFeedContainer
            dateFrom={dateRange.from}
            dateTo={dateRange.to}
            company={normalizedCompany}
          />
        </div>

        {/* Quick Actions and System Overview */}
        <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
          <DashboardQuickActions
            onCreateAwb={handleCreateAwb}
            onAddCustomer={handleAddCustomer}
            onTrackShipment={handleTrackShipment}
            onOpenManifests={handleOpenManifests}
          />
          <DashboardSystemOverview
            overview={dashboardData.overview}
            trends={dashboardData.trends}
            trendIndicators={trendIndicators}
          />
        </div>
      </div>
    );
  }

  return (
    <PrefersReducedMotionContext.Provider value={prefersReducedMotion}>
      {content}
    </PrefersReducedMotionContext.Provider>
  );
};

export default Dashboard;
